import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import "./landing.css";

const DEFAULT_IN_VIEW_OPTIONS = {
  threshold: 0.18,
  rootMargin: "0px 0px -12% 0px",
};

/**
 * Tracks viewport visibility and replays section animations each time the user
 * scrolls back into view. `settled` keeps the end state visible after leaving.
 */
const useRepeatInView = (options = {}) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [settled, setSettled] = useState(false);
  const wasIntersecting = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setActive(true);
      setSettled(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const intersecting = entry.isIntersecting;

          if (intersecting && !wasIntersecting.current) {
            setSettled(false);
            setActive(false);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => setActive(true));
            });
          } else if (!intersecting) {
            setActive(false);
            if (wasIntersecting.current) setSettled(true);
          }

          wasIntersecting.current = intersecting;
        });
      },
      { ...DEFAULT_IN_VIEW_OPTIONS, ...options }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, active, settled];
};

const EXAM_QUESTION =
  "Question 14 — Solve the equation 3x² − 5x − 2 = 0. Give your answers correct to 2 decimal places.";

/** Types out `text` character-by-character while `start` is true; resets when hidden. */
const useTypewriter = (text, start, speed = 32) => {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!start) {
      setTyped("");
      return undefined;
    }

    setTyped("");
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setTyped(text.slice(0, i));
      if (i >= text.length) clearInterval(timer);
    }, speed);
    return () => clearInterval(timer);
  }, [text, start, speed]);

  return typed;
};

const sectionClass = (active, settled) => {
  if (active) return "section-in-view";
  if (settled) return "section-settled";
  return "";
};

const ComingSoon = () => {
  const [heroRef, heroActive, heroSettled] = useRepeatInView({ threshold: 0.2, rootMargin: "0px 0px -8% 0px" });
  const [papersRef, papersActive, papersSettled] = useRepeatInView({ threshold: 0.22 });
  const [examRef, examActive, examSettled] = useRepeatInView({ threshold: 0.28 });
  const [homeworkRef, homeworkActive, homeworkSettled] = useRepeatInView({ threshold: 0.25 });
  const [progressRef, progressActive, progressSettled] = useRepeatInView({ threshold: 0.28 });

  const typedQuestion = useTypewriter(EXAM_QUESTION, examActive);
  const examQuestion = examActive ? typedQuestion : examSettled ? EXAM_QUESTION : "";
  const typingDone = examQuestion.length === EXAM_QUESTION.length;
  const progressRingActive = progressActive || progressSettled;

  return (
    <div className="landing-page">
      <header>
        <div className="nav-inner">
          <div className="brand">
            <img src={logo} alt="Edutopia logo" style={{ width: "160px", height: "90px" }} />
          </div>

          <div className="nav-right">
            <Link to="/login" className="btn btn-ghost" style={{ padding: "10px 22px", fontSize: "14px" }}>
              Log in
            </Link>
            <Link to="/signup" className="btn btn-primary" style={{ padding: "10px 22px", fontSize: "14px" }}>
              Sign up now
            </Link>
          </div>
        </div>
      </header>

      <section className={`hero scroll-section ${sectionClass(heroActive, heroSettled)}`} ref={heroRef}>
        <div className="wrap hero-grid">
          <div className="hero-copy">
            <p className="eyebrow reveal reveal-1">IGCSE preparation, done properly</p>
            <h1 className="reveal reveal-2">
              Your first step
              <br />
              towards <em>success</em>
            </h1>
            <p className="lead reveal reveal-3">
              Structured IGCSE Mathematics courses, real past papers, and marked mock exams — everything you need to
              walk into results day ready.
            </p>
            <div className="hero-cta reveal reveal-4">
              <Link to="/login" className="btn btn-ghost">
                Log in
              </Link>
              <Link to="/signup" className="btn btn-primary">
                Sign up now
              </Link>
            </div>
          </div>
          <div className="paper-stage reveal reveal-5">
            <div className="paper-card paper-behind-2" />
            <div className="paper-card paper-behind-1" />
            <div className={`paper-card paper-main ${heroActive || heroSettled ? "float" : ""}`}>
              <div
                className="ruled"
                style={{ width: "40%", height: "14px", background: "var(--maroon-deep)", opacity: 0.15 }}
              />
              <div style={{ height: "18px" }} />
              <div className="ruled w1" />
              <div className="ruled w2" />
              <div className="ruled w3" />
              <div className="ruled w4" />
              <div style={{ height: "10px" }} />
              <div className="ruled w1" />
              <div className="ruled w2" />
              <div className="ruled" style={{ width: "70%" }} />
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" style={{ marginTop: "18px" }}>
                <path
                  d="M4 13l4.5 4.5L20 6"
                  stroke="#6B0F1E"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="stamp pop">A*</div>
              <div className="badge-tag pop">18 / 20</div>
            </div>
          </div>
        </div>
      </section>

      <div className="trust">
        <div className="trust-inner">
          <p>Trusted by IGCSE students in 90+ countries</p>
          <div className="trust-stats">
            <div className="trust-stat">
              <b>40,000+</b>
              <span>maths past paper questions</span>
            </div>
            <div className="trust-stat">
              <b>0580</b>
              <span>Cambridge IGCSE Mathematics</span>
            </div>
            <div className="trust-stat">
              <b>94%</b>
              <span>of students improve a grade</span>
            </div>
          </div>
        </div>
      </div>

      <section
        className={`block papers scroll-section ${sectionClass(papersActive, papersSettled)}`}
        id="papers"
        ref={papersRef}
      >
        <div className="wrap">
          <div className="section-inner">
            <div className="tab">Past Papers</div>
            <div className="papers-visual">
              <div className="pp-card pp3 reveal-card reveal-card-1">
                <div className="pp-head">
                  <span>MAY/JUN 2022</span>
                  <span>P4</span>
                </div>
                <div className="pp-line" style={{ width: "80%" }} />
                <div className="pp-line" style={{ width: "65%" }} />
                <div className="pp-line" style={{ width: "70%" }} />
              </div>
              <div className="pp-card pp2 reveal-card reveal-card-2">
                <div className="pp-head">
                  <span>OCT/NOV 2023</span>
                  <span>P2</span>
                </div>
                <div className="pp-line" style={{ width: "85%" }} />
                <div className="pp-line" style={{ width: "60%" }} />
                <div className="pp-line" style={{ width: "75%" }} />
              </div>
              <div className="pp-card pp1 reveal-card reveal-card-3">
                <div className="pp-head">
                  <span>MAY/JUN 2025</span>
                  <span>P1</span>
                </div>
                <div className="pp-line" style={{ width: "90%" }} />
                <div className="pp-line" style={{ width: "55%" }} />
                <div className="pp-line" style={{ width: "68%" }} />
              </div>
            </div>
            <div className="papers-copy">
              <p className="eyebrow reveal reveal-1">Past-paper practice</p>
              <h2 className="reveal reveal-2">Ten years of real maths papers, marked the way examiners mark them</h2>
              <p className="reveal reveal-3">
                Work through every Cambridge 0580 session back to 2015, timed or untimed, with the official mark scheme
                sitting right next to your answer.
              </p>
              <ul className="check-list">
                <li className="reveal reveal-4">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#6B0F1E" />
                    <path
                      d="M7 12.5l3 3 7-7"
                      stroke="#F6F0E4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Full mark schemes with examiner-style comments on every answer
                </li>
                <li className="reveal reveal-5">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#6B0F1E" />
                    <path
                      d="M7 12.5l3 3 7-7"
                      stroke="#F6F0E4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Filter by topic to drill your weakest maths areas only
                </li>
                <li className="reveal reveal-6">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="11" fill="#6B0F1E" />
                    <path
                      d="M7 12.5l3 3 7-7"
                      stroke="#F6F0E4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Grade boundaries shown alongside every session
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className={`block exams scroll-section ${sectionClass(examActive, examSettled)}`} id="exams" ref={examRef}>
        <div className="wrap">
          <div className="section-inner">
            <div className="tab">Mock Exams</div>
            <div className="reveal reveal-1" style={{ flex: 1 }}>
              <p className="eyebrow">Exams</p>
              <h2>Sit a full maths mock, under real exam conditions</h2>
              <p style={{ marginBottom: 0, maxWidth: "460px" }}>
                Timed sittings that mirror the real thing — same paper structure, same command words, same clock. Get a
                predicted grade the moment you finish.
              </p>
            </div>
            <div className="exam-mock reveal reveal-2">
              <div className="exam-top">
                <span className="mono" style={{ fontSize: "12px", color: "var(--ink-soft)" }}>
                  MATHEMATICS · PAPER 2
                </span>
                <span className="exam-timer">42:18 left</span>
              </div>
              <div className="exam-progress">
                <span className="done" />
                <span className="done" />
                <span className="done" />
                <span />
                <span />
                <span />
              </div>
              <p className="exam-q">
                {examQuestion}
                <span className={`type-caret ${typingDone ? "type-caret-done" : ""}`} aria-hidden="true" />
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={`block homework scroll-section ${sectionClass(homeworkActive, homeworkSettled)}`} id="homework" ref={homeworkRef}>
        <div className="wrap">
          <div className="section-inner">
            <div className="tab">Homework</div>
            <div className="reveal reveal-1" style={{ flex: 1 }}>
              <p className="eyebrow">Homework</p>
              <h2>Set by your teacher, or set your own</h2>
              <p style={{ maxWidth: "460px" }}>
                Every maths assignment lands in one place, with a due date and a straight line back to the topic it&apos;s
                testing — so nothing gets lost between class and revision.
              </p>
            </div>
            <div className="hw-card">
              <div className="hw-row hw-reveal hw-reveal-1">
                <div className="hw-check done">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4 13l4.5 4.5L20 6"
                      stroke="#F6F0E4"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="hw-text">
                  <b>Algebraic fractions — worksheet 4</b>
                  <span>Mathematics · 0580</span>
                </div>
                <span className="hw-tag">Done</span>
              </div>
              <div className="hw-row hw-reveal hw-reveal-2">
                <div className="hw-check" />
                <div className="hw-text">
                  <b>Quadratic equations past-paper set</b>
                  <span>Mathematics · 0580</span>
                </div>
                <span className="hw-tag due">Due tomorrow</span>
              </div>
              <div className="hw-row hw-reveal hw-reveal-3">
                <div className="hw-check" />
                <div className="hw-text">
                  <b>Trigonometry — sine &amp; cosine rule</b>
                  <span>Mathematics · 0580</span>
                </div>
                <span className="hw-tag">Due Fri</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={`block progress scroll-section ${sectionClass(progressActive, progressSettled)}`} id="progress" ref={progressRef}>
        <div className="wrap">
          <div className="section-head">
            <div className="copy reveal reveal-1">
              <p className="eyebrow">Progress tracking</p>
              <h2>Watch your maths grade take shape, topic by topic</h2>
            </div>
          </div>
          <div className="section-inner">
            <div className="tab">Progress</div>
            <div className="grade-ring-wrap reveal reveal-2">
              <svg width="220" height="220" viewBox="0 0 220 220" className={progressActive ? "ring-animate" : ""}>
                <circle cx="110" cy="110" r="92" fill="none" stroke="#DFCEA9" strokeWidth="16" />
                <circle
                  className="ring-progress"
                  cx="110"
                  cy="110"
                  r="92"
                  fill="none"
                  stroke="#6B0F1E"
                  strokeWidth="16"
                  strokeLinecap="round"
                  strokeDasharray="578"
                  strokeDashoffset={progressRingActive ? "98" : "578"}
                  transform="rotate(-90 110 110)"
                />
                <text
                  x="110"
                  y="102"
                  textAnchor="middle"
                  fontFamily="Fraunces, serif"
                  fontWeight="700"
                  fontSize="46"
                  fill="#3D0810"
                  className="ring-label"
                >
                  A*
                </text>
                <text
                  x="110"
                  y="128"
                  textAnchor="middle"
                  fontFamily="IBM Plex Mono, monospace"
                  fontSize="12"
                  fill="#7A6C5D"
                  className="ring-label"
                >
                  PREDICTED GRADE
                </text>
              </svg>
            </div>
            <div className="mastery reveal reveal-3">
              <div className="mastery-row">
                <span className="m-label">Algebra</span>
                <div className="mastery-bar">
                  <span style={{ "--target-width": "92%" }} />
                </div>
                <span className="m-pct">92%</span>
              </div>
              <div className="mastery-row">
                <span className="m-label">Number</span>
                <div className="mastery-bar">
                  <span style={{ "--target-width": "88%" }} />
                </div>
                <span className="m-pct">88%</span>
              </div>
              <div className="mastery-row">
                <span className="m-label">Geometry</span>
                <div className="mastery-bar">
                  <span style={{ "--target-width": "81%" }} />
                </div>
                <span className="m-pct">81%</span>
              </div>
              <div className="mastery-row">
                <span className="m-label">Trigonometry</span>
                <div className="mastery-bar">
                  <span style={{ "--target-width": "74%" }} />
                </div>
                <span className="m-pct">74%</span>
              </div>
              <div className="mastery-row">
                <span className="m-label">Statistics</span>
                <div className="mastery-bar">
                  <span style={{ "--target-width": "85%" }} />
                </div>
                <span className="m-pct">85%</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="cta-band">
        <div className="wrap">
          <p className="eyebrow" style={{ color: "#E7C9C9" }}>
            Get started
          </p>
          <h2>Results day starts today</h2>
          <p>Join thousands of IGCSE students already mastering Mathematics, one marked paper at a time.</p>
          <div className="hero-cta">
            <Link to="/signup" className="btn btn-inverse">
              Sign up now
            </Link>
            <Link to="/login" className="btn btn-ghost" style={{ borderColor: "rgba(247,242,236,0.35)", color: "var(--cream)" }}>
              Log in
            </Link>
          </div>
        </div>
      </div>

      <footer>
        <div className="wrap">
          <div className="footer-brand" style={{ marginBottom: "48px" }}>
            <img src={logo} alt="Edutopia logo" />
            <p>
              Structured IGCSE Mathematics courses, past papers, and mock exams — built for students working towards
              results day, wherever they are.
            </p>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Edutopia. All rights reserved.</p>
            <p>
              Cambridge IGCSE® is a registered trademark of Cambridge Assessment International Education. Edutopia is an
              independent platform and is not affiliated with or endorsed by Cambridge Assessment.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComingSoon;
